import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTerminal, faFile, faKey, faNetworkWired, faCamera, faShield, faCog,
  faUpload, faDownload, faFolder, faFileAlt, faTrash, faFolderPlus,
  faShieldAlt, faExpand, faList, faKeyboard, faSatelliteDish
} from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';

const BeaconContextMenu = ({ beacon, position, onClose, onTaskCreated }) => {
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [taskParams, setTaskParams] = useState({});
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  // Task categories with icons and commands
  const taskCategories = [
    {
      name: 'Execution',
      icon: faTerminal,
      commands: [
        { name: 'shell', label: 'Run Shell Command', icon: faTerminal, requiresInput: true, inputType: 'text', inputLabel: 'Command' },
        { name: 'powershell', label: 'Run PowerShell', icon: faTerminal, requiresInput: true, inputType: 'textarea', inputLabel: 'PowerShell Script' },
        { name: 'execute_assembly', label: 'Execute .NET Assembly', icon: faFileAlt, requiresInput: true, inputType: 'file', inputLabel: 'Assembly Path' }
      ]
    },
    {
      name: 'File Operations',
      icon: faFile,
      commands: [
        { name: 'ls', label: 'List Directory', icon: faFolder, requiresInput: true, inputType: 'text', inputLabel: 'Path (optional)', optional: true },
        { name: 'cat', label: 'Read File', icon: faFileAlt, requiresInput: true, inputType: 'text', inputLabel: 'File Path' },
        { name: 'download', label: 'Download File', icon: faDownload, requiresInput: true, inputType: 'text', inputLabel: 'Remote Path' },
        { name: 'upload', label: 'Upload File', icon: faUpload, requiresInput: true, inputType: 'file', inputLabel: 'Local File' },
        { name: 'mkdir', label: 'Create Directory', icon: faFolderPlus, requiresInput: true, inputType: 'text', inputLabel: 'Directory Path' },
        { name: 'rm', label: 'Delete File/Directory', icon: faTrash, requiresInput: true, inputType: 'text', inputLabel: 'Path' }
      ]
    },
    {
      name: 'Credentials',
      icon: faKey,
      commands: [
        { name: 'mimikatz', label: 'Run Mimikatz', icon: faKey, requiresInput: true, inputType: 'text', inputLabel: 'Mimikatz Command', placeholder: 'sekurlsa::logonpasswords' },
        { name: 'lsass_dump', label: 'Dump LSASS Memory', icon: faShieldAlt, requiresInput: false },
        { name: 'dcsync', label: 'DCSync Attack', icon: faNetworkWired, requiresInput: true, inputType: 'text', inputLabel: 'Target User' },
        { name: 'kerberoast', label: 'Kerberoast SPNs', icon: faKey, requiresInput: false }
      ]
    },
    {
      name: 'Lateral Movement',
      icon: faNetworkWired,
      commands: [
        { name: 'psexec', label: 'PSExec (Remote Exec)', icon: faTerminal, requiresInput: true, inputType: 'multifield', fields: [
          { name: 'target', label: 'Target Host', type: 'text' },
          { name: 'command', label: 'Command', type: 'text' }
        ]},
        { name: 'wmi_exec', label: 'WMI Exec', icon: faTerminal, requiresInput: true, inputType: 'multifield', fields: [
          { name: 'target', label: 'Target Host', type: 'text' },
          { name: 'command', label: 'Command', type: 'text' }
        ]},
        { name: 'schtasks', label: 'Scheduled Task', icon: faCog, requiresInput: true, inputType: 'multifield', fields: [
          { name: 'target', label: 'Target Host', type: 'text' },
          { name: 'taskName', label: 'Task Name', type: 'text' },
          { name: 'command', label: 'Command', type: 'text' }
        ]}
      ]
    },
    {
      name: 'Enumeration',
      icon: faExpand,
      commands: [
        { name: 'screenshot', label: 'Capture Screenshot', icon: faCamera, requiresInput: false },
        { name: 'keylog_start', label: 'Start Keylogger', icon: faKeyboard, requiresInput: false },
        { name: 'keylog_stop', label: 'Stop Keylogger', icon: faKeyboard, requiresInput: false },
        { name: 'process_list', label: 'List Processes', icon: faList, requiresInput: false },
        { name: 'network_scan', label: 'Network Scan', icon: faSatelliteDish, requiresInput: true, inputType: 'text', inputLabel: 'Target Range (CIDR)', placeholder: '192.168.1.0/24' }
      ]
    },
    {
      name: 'Evasion',
      icon: faShield,
      commands: [
        { name: 'patch_amsi', label: 'Patch AMSI (Windows)', icon: faShieldAlt, requiresInput: false },
        { name: 'patch_etw', label: 'Patch ETW (Windows)', icon: faShieldAlt, requiresInput: false },
        { name: 'patch_both', label: 'Patch AMSI + ETW', icon: faShieldAlt, requiresInput: false }
      ]
    },
    {
      name: 'Control',
      icon: faCog,
      commands: [
        { name: 'sleep', label: 'Change Sleep Interval', icon: faCog, requiresInput: true, inputType: 'number', inputLabel: 'Sleep (seconds)' },
        { name: 'jitter', label: 'Change Jitter', icon: faCog, requiresInput: true, inputType: 'number', inputLabel: 'Jitter (%)' },
        { name: 'kill', label: 'Kill Beacon', icon: faShieldAlt, requiresInput: false, dangerous: true },
        { name: 'exit', label: 'Exit Gracefully', icon: faShieldAlt, requiresInput: false }
      ]
    }
  ];

  const handleCommandClick = (command) => {
    setSelectedCommand(command);
    setTaskParams({});
    setError(null);

    if (!command.requiresInput) {
      // No input required - create task immediately
      createTask(command.name, {});
    } else {
      // Show modal for input
      setShowTaskModal(true);
    }
    onClose();
  };

  const createTask = async (commandName, params) => {
    setCreating(true);
    setError(null);

    try {
      const taskData = {
        beacon: beacon._id,
        command: commandName,
        args: params
      };

      await c2Service.createBeaconTask(taskData);
      setShowTaskModal(false);
      if (onTaskCreated) onTaskCreated();
    } catch (error) {
      console.error('Failed to create task:', error);
      setError(error.response?.data?.error || error.message || 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const handleTaskSubmit = (e) => {
    e.preventDefault();
    createTask(selectedCommand.name, taskParams);
  };

  const renderInputField = (command) => {
    if (command.inputType === 'multifield') {
      return command.fields.map((field, idx) => (
        <Form.Group key={idx} className="mb-3">
          <Form.Label>{field.label}</Form.Label>
          <Form.Control
            type={field.type}
            value={taskParams[field.name] || ''}
            onChange={(e) => setTaskParams({ ...taskParams, [field.name]: e.target.value })}
            required
          />
        </Form.Group>
      ));
    }

    if (command.inputType === 'textarea') {
      return (
        <Form.Group className="mb-3">
          <Form.Label>{command.inputLabel}</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            value={taskParams.value || ''}
            onChange={(e) => setTaskParams({ value: e.target.value })}
            placeholder={command.placeholder}
            required={!command.optional}
          />
        </Form.Group>
      );
    }

    if (command.inputType === 'file') {
      return (
        <Form.Group className="mb-3">
          <Form.Label>{command.inputLabel}</Form.Label>
          <Form.Control
            type="text"
            value={taskParams.filePath || ''}
            onChange={(e) => setTaskParams({ filePath: e.target.value })}
            placeholder="Enter file path"
            required
          />
          <Form.Text className="text-muted">
            For upload: local file path on C2 server. For execute_assembly: path to .NET assembly.
          </Form.Text>
        </Form.Group>
      );
    }

    return (
      <Form.Group className="mb-3">
        <Form.Label>{command.inputLabel}</Form.Label>
        <Form.Control
          type={command.inputType || 'text'}
          value={taskParams.value || ''}
          onChange={(e) => setTaskParams({ value: e.target.value })}
          placeholder={command.placeholder}
          required={!command.optional}
        />
      </Form.Group>
    );
  };

  return (
    <>
      {/* Context Menu */}
      <div
        style={{
          position: 'fixed',
          top: position.y,
          left: position.x,
          backgroundColor: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          minWidth: '250px',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '0.75rem',
          backgroundColor: '#111827',
          borderBottom: '1px solid #374151',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <div style={{ color: '#f9fafb', fontWeight: 'bold', fontSize: '0.875rem' }}>
            {beacon.hostname || beacon.internalIp}
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
            {beacon.systemInfo?.os} • {beacon.systemInfo?.username}
          </div>
        </div>

        {/* Task Categories */}
        {taskCategories.map((category, catIdx) => (
          <div key={catIdx}>
            <div style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: '#111827',
              color: '#9ca3af',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              borderTop: catIdx > 0 ? '1px solid #374151' : 'none'
            }}>
              {category.name}
            </div>
            {category.commands.map((command, cmdIdx) => (
              <div
                key={cmdIdx}
                onClick={() => handleCommandClick(command)}
                style={{
                  padding: '0.5rem 0.75rem',
                  cursor: 'pointer',
                  color: command.dangerous ? '#ef4444' : '#e5e7eb',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <FontAwesomeIcon icon={command.icon} style={{ width: '14px' }} />
                <span>{command.label}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Task Input Modal */}
      {showTaskModal && selectedCommand && (
        <Modal show={showTaskModal} onHide={() => setShowTaskModal(false)} centered>
          <Modal.Header closeButton style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151' }}>
            <Modal.Title style={{ color: '#f9fafb', fontSize: '1.125rem' }}>
              <FontAwesomeIcon icon={selectedCommand.icon} className="me-2" />
              {selectedCommand.label}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#111827', color: '#f9fafb' }}>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Form onSubmit={handleTaskSubmit}>
              <div className="mb-3">
                <strong>Target Beacon:</strong> {beacon.hostname || beacon.internalIp} ({beacon.systemInfo?.os})
              </div>

              {renderInputField(selectedCommand)}

              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setShowTaskModal(false)}
                  disabled={creating}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating...
                    </>
                  ) : (
                    <>Create Task</>
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default BeaconContextMenu;
